import { useState, useEffect, useRef } from 'react';
import { Browser } from '@capacitor/browser';
import type { Theme } from '../lib/tokens';
import { FONTS } from '../lib/tokens';
import { Logo, SoftButton } from '../components/common';
import { Icon } from '../components/icons';
import { useAuth } from '../hooks/useAuth';
import { hapticSelect } from '../lib/haptics';
import { getStoredInviteToken, clearInviteToken, storeInviteToken, normalizeInviteToken } from '../lib/invite';
import { ETHNICITY_OPTIONS, COUNTRIES } from '../lib/identity';
import { SIGNUP_PATH_KEY } from '../lib/signupPath';
import { track, useScreenView } from '../lib/analytics';
import { preparePhoto, uploadPrepared, PhotoError, type PreparedPhoto } from '../lib/photos';

// Analytics names for each step, keyed by the step element's React key.
const STEP_NAMES: Record<string, string> = {
  w: 'welcome', mode: 'mode', n: 'basics', gender: 'gender', seeking: 'seeking',
  taste: 'taste', loc: 'location', spark: 'spark', photo: 'photo', invite: 'invite_partner',
};

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8080';

const ACTIVITY_CATEGORIES = [
  { key: 'food', label: 'Food & Drink' },
  { key: 'arts', label: 'Arts & Culture' },
  { key: 'outdoor', label: 'Outdoor' },
  { key: 'nightlife', label: 'Nightlife' },
  { key: 'wellness', label: 'Wellness' },
];

const VIBE_OPTIONS = [
  { key: 'romantic', label: 'Romantic', sub: 'Intimate, meaningful', vector: { romantic: 5, adventurous: 2, relaxed: 2, social: 1 } },
  { key: 'adventurous', label: 'Adventurous', sub: 'Trying new things', vector: { romantic: 1, adventurous: 5, relaxed: 2, social: 2 } },
  { key: 'relaxed', label: 'Laid-back', sub: 'Easy conversation', vector: { romantic: 2, adventurous: 1, relaxed: 5, social: 2 } },
  { key: 'social', label: 'Social', sub: 'Lively, fun energy', vector: { romantic: 1, adventurous: 2, relaxed: 2, social: 5 } },
];

const BUDGET_OPTIONS = [
  { value: 20, label: '£20', sub: 'Coffee & a walk' },
  { value: 40, label: '£40', sub: 'Casual dinner' },
  { value: 70, label: '£70', sub: 'Proper night out' },
  { value: 100, label: '£100+', sub: 'Special occasion' },
];

const SPARK_PROMPTS = [
  'My idea of a perfect Sunday',
  'A place in London that feels like mine',
  'You should know about me that',
];

interface OnboardingProps {
  t: Theme;
  onFinish: (opts?: { inviteExpired?: boolean }) => void;
}

type Gender = 'man' | 'woman' | 'nonbinary' | 'other';
type SignupMode = 'solo' | 'couple' | '';

function isValidEmail(v: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
}

export function Onboarding({ t, onFinish }: OnboardingProps) {
  const { getIdToken } = useAuth();
  const [step, setStep] = useState(0);

  // University welcome. Asks the server whether the VERIFIED email on this
  // session is a university one (the server holds the allowlist). If they
  // chose the university route but signed up with another address, say so
  // gently — they're a normal user, nothing is blocked.
  const [university, setUniversity] = useState<{ domain: string; name: string | null } | null>(null);
  const [universityMismatch, setUniversityMismatch] = useState(false);
  useEffect(() => {
    let choseUniversity = false;
    try { choseUniversity = sessionStorage.getItem(SIGNUP_PATH_KEY) === 'university'; } catch { /* storage blocked */ }
    (async () => {
      try {
        const token = await getIdToken();
        if (!token) return;
        const res = await fetch(`${API_BASE}/api/auth/university`, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) return;
        const data = await res.json();
        if (data.university) setUniversity(data.university);
        else if (choseUniversity) setUniversityMismatch(true);
      } catch { /* welcome is cosmetic — never block onboarding on it */ }
    })();
  }, []);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const [agreedToTerms, setAgreedToTerms] = useState(false);

  const [displayName, setDisplayName] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [signupMode, setSignupMode] = useState<SignupMode>('');
  const [gender, setGender] = useState<Gender | ''>('');
  const [ethnicity, setEthnicity] = useState('');
  const [nationality, setNationality] = useState('');
  const [seekingGenders, setSeeking] = useState<Gender[]>([]);
  const [activities, setActivities] = useState<Record<string, number>>(
    Object.fromEntries(ACTIVITY_CATEGORIES.map(c => [c.key, 3]))
  );
  const [budget, setBudget] = useState(40);
  const [vibeKeys, setVibeKeys] = useState<string[]>(['romantic']);
  const [postcode, setPostcode] = useState('');
  const [radiusValue, setRadiusValue] = useState(10);
  const [distanceUnit, setDistanceUnit] = useState<'miles' | 'km'>('miles');
  const [answers, setAnswers] = useState(['', '', '']);
  const [partnerEmail, setPartnerEmail] = useState('');

  // Required profile photo. The account doesn't exist until handleFinish, and
  // the upload URL is per-user, so the photo is prepared (resized, metadata
  // stripped) when picked and only uploaded after /register.
  const [photo, setPhoto] = useState<{ prepared: PreparedPhoto; previewUrl: string } | null>(null);
  const [photoPreparing, setPhotoPreparing] = useState(false);
  // If a later call fails and they retry, reuse the upload instead of making another.
  const uploadedPhoto = useRef<{ prepared: PreparedPhoto; url: string } | null>(null);
  useEffect(() => () => { if (photo) URL.revokeObjectURL(photo.previewUrl); }, [photo]);

  const pickPhoto = async (file: File) => {
    setError('');
    setPhotoPreparing(true);
    try {
      const prepared = await preparePhoto(file);
      setPhoto({ prepared, previewUrl: URL.createObjectURL(prepared.blob) });
      track('onboarding_photo_added');
    } catch (err) {
      setError(err instanceof PhotoError ? err.message : 'We couldn\'t read that photo. Please try another.');
    } finally {
      setPhotoPreparing(false);
    }
  };

  // ── Invite-your-partner (couples day-one signup) ──────────────────
  const [inviteToken] = useState(() => getStoredInviteToken());
  const [isInvitee, setIsInvitee] = useState(!!inviteToken);
  const [inviteChecked, setInviteChecked] = useState(!inviteToken);
  const [inviteInfo, setInviteInfo] = useState<{ inviterName: string; inviterPhoto: string | null } | null>(null);
  const [showCodeInput, setShowCodeInput] = useState(false);
  const [codeValue, setCodeValue] = useState('');
  const [codeError, setCodeError] = useState('');
  const [codeChecking, setCodeChecking] = useState(false);

  useEffect(() => {
    if (!inviteToken) return;
    fetch(`${API_BASE}/api/couples/invite/info?token=${encodeURIComponent(inviteToken)}`)
      .then(r => r.json())
      .then(data => {
        if (data.valid) {
          setInviteInfo({ inviterName: data.inviterName, inviterPhoto: data.inviterPhoto ?? null });
        } else {
          clearInviteToken();
          setIsInvitee(false);
        }
      })
      .catch(() => { clearInviteToken(); setIsInvitee(false); })
      .finally(() => setInviteChecked(true));
  }, [inviteToken]);

  const submitCode = async () => {
    const token = normalizeInviteToken(codeValue);
    if (!token) { setCodeError('Enter a code.'); return; }
    setCodeChecking(true);
    setCodeError('');
    try {
      const res = await fetch(`${API_BASE}/api/couples/invite/info?token=${encodeURIComponent(token)}`);
      const data = await res.json();
      if (data.valid) {
        storeInviteToken(token);
        setInviteInfo({ inviterName: data.inviterName, inviterPhoto: data.inviterPhoto ?? null });
        setIsInvitee(true);
        setInviteChecked(true);
        setShowCodeInput(false);
      } else {
        setCodeError("That code doesn't look right — check with your partner.");
      }
    } catch {
      setCodeError('Something went wrong — try again.');
    } finally {
      setCodeChecking(false);
    }
  };

  const showInvitePartnerStep = signupMode === 'couple' && !isInvitee;
  const totalSteps = showInvitePartnerStep ? 10 : 9;

  const canProceed = () => {
    if (step === 0) return agreedToTerms;
    if (step === 1) {
      if (isInvitee) return inviteChecked;
      return signupMode !== '';
    }
    if (step === 2) {
      if (displayName.trim().length < 2 || dateOfBirth.length !== 10) return false;
      return calendarAge(dateOfBirth) >= 18;
    }
    if (step === 3) return gender !== '';
    if (step === 4) return seekingGenders.length > 0;
    if (step === 6) return postcode.trim().length >= 2;
    if (step === 7) return answers.every(a => a.trim().length > 0);
    if (step === 8) return !!photo && !photoPreparing;
    if (step === 9) return partnerEmail === '' || isValidEmail(partnerEmail);
    return true;
  };

  const handleFinish = async () => {
    setSubmitting(true);
    setError('');
    try {
      const token = await getIdToken();
      if (!token) throw new Error('Not authenticated');

      // Inviter registers COUPLES immediately (leaves the singles feed right away).
      // Invitee registers SINGLES — if redemption fails below, they're still a
      // normal, working account rather than stranded in couples mode alone.
      const registerMode = showInvitePartnerStep ? 'COUPLES' : 'SINGLES';

      const regRes = await fetch(`${API_BASE}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ displayName: displayName.trim(), dateOfBirth, mode: registerMode }),
      });
      if (!regRes.ok && regRes.status !== 200) {
        const err = await regRes.json().catch(() => ({}));
        throw new Error(err.error || 'Registration failed');
      }

      if (!photo) throw new Error('Please add a photo of yourself.');
      if (uploadedPhoto.current?.prepared !== photo.prepared) {
        uploadedPhoto.current = { prepared: photo.prepared, url: await uploadPrepared(photo.prepared, token) };
      }

      let lat = 51.5074, lng = -0.1278;
      try {
        const geo = await fetch(`https://api.postcodes.io/postcodes/${encodeURIComponent(postcode.trim())}`);
        if (geo.ok) { const d = await geo.json(); lat = d.result.latitude; lng = d.result.longitude; }
      } catch { /* use London centre */ }

      const dims = ['romantic', 'adventurous', 'relaxed', 'social'] as const;
      const vibeVector = Object.fromEntries(dims.map(d => [
        d,
        vibeKeys.reduce((sum, k) => sum + (VIBE_OPTIONS.find(v => v.key === k)!.vector[d] ?? 0), 0) / vibeKeys.length,
      ]));

      const onbRes = await fetch(`${API_BASE}/api/auth/onboarding`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          gender,
          seekingGenders,
          activityPreferences: activities,
          budgetMidpoint: budget,
          vibeVector,
          locationPostcode: postcode.trim().toUpperCase(),
          locationLat: lat,
          locationLng: lng,
          locationRadiusMiles: distanceUnit === 'km' ? Math.round(radiusValue * 0.621371) : radiusValue,
          sparkAnswers: SPARK_PROMPTS.map((prompt, i) => ({ prompt, answer: answers[i].trim() })),
          ...(ethnicity && { ethnicity }),
          ...(nationality && { nationality }),
          photos: [uploadedPhoto.current.url],
        }),
      });
      if (!onbRes.ok) {
        const err = await onbRes.json().catch(() => ({}));
        // The backend's generic "Validation failed" hides which field tripped —
        // log the Zod issue list so this is debuggable from the console instead
        // of a dead end (e.g. the locationRadiusMiles km->miles rounding bug).
        if (err.details) console.error('[onboarding] validation details:', err.details);
        throw new Error(err.error || 'Failed to save profile');
      }

      let inviteExpired = false;

      if (showInvitePartnerStep) {
        // Inviter: create the partner invite. Non-fatal — the waiting screen
        // in Discover can create one if this fails.
        try {
          await fetch(`${API_BASE}/api/couples/invite`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ email: partnerEmail.trim() || undefined }),
          });
        } catch { /* recoverable from the waiting screen */ }
      } else if (isInvitee && inviteToken) {
        // Invitee: redeem the invite. On any failure, degrade gracefully to a
        // normal singles account (registerMode was already SINGLES above).
        try {
          const redeemRes = await fetch(`${API_BASE}/api/couples/invite/redeem`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ token: inviteToken }),
          });
          if (!redeemRes.ok) inviteExpired = true;
        } catch {
          inviteExpired = true;
        }
        clearInviteToken();
      }

      try { sessionStorage.removeItem(SIGNUP_PATH_KEY); } catch { /* storage blocked */ }
      track('sign_up', { mode: registerMode.toLowerCase(), university: !!university, invited: isInvitee });
      onFinish(inviteExpired ? { inviteExpired: true } : undefined);
    } catch (err: any) {
      track('sign_up_failed');
      setError(err.message || 'Something went wrong');
      setSubmitting(false);
    }
  };

  const next = () => {
    if (submitting) return;
    if (!canProceed()) {
      if (step === 0) setError('Please accept the Terms & Conditions to continue.');
      else if (step === 1 && !isInvitee) setError('Please choose how you\'re here.');
      else if (step === 2) {
        if (displayName.trim().length < 2) setError('Please enter your name.');
        else if (dateOfBirth.length !== 10) setError('Please enter your date of birth.');
        else setError('You must be 18 or older to join Preminade.');
      }
      else if (step === 8) { if (!photoPreparing) setError('Please add a photo of yourself.'); }
      else if (step === 9) setError('Please enter a valid email address.');
      track('onboarding_blocked', { step: step + 1, step_name: stepName });
      return;
    }
    setError('');
    if (step + 1 >= totalSteps) handleFinish(); else setStep(s => s + 1);
  };
  const back = () => step > 0 && setStep(s => s - 1);

  const steps = [
    <OnbWelcome t={t} key="w" agreedToTerms={agreedToTerms} setAgreedToTerms={setAgreedToTerms} university={university} universityMismatch={universityMismatch} />,
    <OnbMode
      t={t} key="mode"
      signupMode={signupMode} setSignupMode={setSignupMode}
      isInvitee={isInvitee} inviteChecked={inviteChecked} inviteInfo={inviteInfo}
      showCodeInput={showCodeInput} setShowCodeInput={setShowCodeInput}
      codeValue={codeValue} setCodeValue={setCodeValue}
      codeError={codeError} codeChecking={codeChecking} onSubmitCode={submitCode}
    />,
    <OnbBasics t={t} key="n" displayName={displayName} setDisplayName={setDisplayName} dateOfBirth={dateOfBirth} setDateOfBirth={setDateOfBirth} isCouple={signupMode === 'couple' || isInvitee} />,
    <OnbGender t={t} key="gender" gender={gender} setGender={setGender} ethnicity={ethnicity} setEthnicity={setEthnicity} nationality={nationality} setNationality={setNationality} />,
    <OnbSeeking t={t} key="seeking" seekingGenders={seekingGenders} setSeeking={setSeeking} />,
    <OnbTaste t={t} key="taste" activities={activities} setActivities={setActivities} budget={budget} setBudget={setBudget} vibeKeys={vibeKeys} setVibeKeys={setVibeKeys} />,
    <OnbLocation t={t} key="loc" postcode={postcode} setPostcode={setPostcode} radiusValue={radiusValue} setRadiusValue={setRadiusValue} distanceUnit={distanceUnit} setDistanceUnit={setDistanceUnit} />,
    <OnbSpark t={t} key="spark" answers={answers} setAnswers={setAnswers} />,
    <OnbPhoto t={t} key="photo" previewUrl={photo?.previewUrl ?? null} preparing={photoPreparing} onPick={pickPhoto} />,
    ...(showInvitePartnerStep ? [<OnbInvitePartner t={t} key="invite" partnerEmail={partnerEmail} setPartnerEmail={setPartnerEmail} />] : []),
  ];

  // One virtual screen per step (the whole wizard is a single URL), e.g.
  // "onboarding/03_basics" — this is what the GA funnel and Clarity filter on.
  const stepName = STEP_NAMES[String((steps[step] as { key?: unknown } | undefined)?.key)] ?? `step${step + 1}`;
  useScreenView(`onboarding/${String(step + 1).padStart(2, '0')}_${stepName}`);

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: t.paper }}>
      <div style={{ padding: '4px 20px 0', display: 'flex', gap: 5, flexShrink: 0 }}>
        {Array.from({ length: totalSteps }).map((_, i) => (
          <div key={i} style={{ flex: 1, height: 3, borderRadius: 2, background: i <= step ? t.ink : t.line, transition: 'background 200ms' }} />
        ))}
      </div>
      <div style={{ padding: '10px 20px 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
        {step > 0 ? (
          <button onClick={back} style={{ all: 'unset', cursor: 'pointer', width: 36, height: 36, borderRadius: 100, background: t.card, border: `1px solid ${t.line}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {Icon.back({ s: 16, c: t.ink })}
          </button>
        ) : <div style={{ width: 36 }} />}
        <span style={{ fontFamily: FONTS.mono, fontSize: 11, color: t.inkMute, letterSpacing: 1 }}>
          {String(step + 1).padStart(2, '0')} / {String(totalSteps).padStart(2, '0')}
        </span>
        <div style={{ width: 36 }} />
      </div>

      <div style={{ flex: 1, overflowY: 'auto' }} className="scroll-container">
        {steps[step]}
      </div>

      {error && (
        <div data-testid="onb-error" style={{ padding: '0 24px 8px', fontFamily: FONTS.sans, fontSize: 13, color: t.terracotta, textAlign: 'center' }}>
          {error}
        </div>
      )}

      <div style={{ padding: '14px 24px 28px', flexShrink: 0 }}>
        <SoftButton t={t} full onClick={next} icon={Icon.arrow({ s: 18, c: t.paper })}>
          {submitting ? 'Saving...' : step === totalSteps - 1 ? 'Start browsing' : 'Continue'}
        </SoftButton>
      </div>
    </div>
  );
}

function OnbWelcome({ t, agreedToTerms, setAgreedToTerms, university, universityMismatch }: {
  t: Theme; agreedToTerms: boolean; setAgreedToTerms: (v: boolean) => void;
  university: { domain: string; name: string | null } | null; universityMismatch: boolean;
}) {
  return (
    <div style={{ padding: '40px 28px', display: 'flex', flexDirection: 'column', gap: 24 }}>
      <div style={{ marginBottom: 8 }}><Logo t={t} size={1.6} /></div>
      {university && (
        <div data-testid="onb-university-welcome" style={{ display: 'flex', alignItems: 'center', gap: 13, padding: '14px 16px', borderRadius: 18, background: `${t.terracotta}0F`, border: `1px solid ${t.terracotta}40` }}>
          <div style={{ width: 40, height: 40, borderRadius: 100, flexShrink: 0, background: t.card, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {Icon.cap({ s: 21, c: t.terracotta })}
          </div>
          <div>
            <div style={{ fontFamily: FONTS.mono, fontSize: 10, letterSpacing: 1.4, textTransform: 'uppercase', color: t.terracotta }}>University email confirmed</div>
            <div style={{ fontFamily: FONTS.display, fontStyle: 'italic', fontSize: 20, color: t.ink, marginTop: 2, lineHeight: 1.15 }}>
              {university.name ? `Welcome from ${university.name}.` : 'Welcome — good to have you.'}
            </div>
          </div>
        </div>
      )}
      {universityMismatch && (
        <div data-testid="onb-university-mismatch" style={{ padding: '12px 15px', borderRadius: 14, background: t.paperAlt, fontFamily: FONTS.sans, fontSize: 13, lineHeight: 1.5, color: t.inkSoft }}>
          That email isn't a university one, so you're set up as normal — everything else works exactly the same.
        </div>
      )}
      <h1 style={{ fontFamily: FONTS.display, fontStyle: 'italic', fontWeight: 400, fontSize: 44, margin: 0, color: t.ink, letterSpacing: -0.6, lineHeight: 1.02 }}>
        Find your Person.<br />Find your People.
      </h1>
      <p style={{ fontFamily: FONTS.sans, fontSize: 15, lineHeight: 1.5, color: t.inkSoft, margin: 0, maxWidth: 320 }}>
        Less texting, more connecting. We thoughtfully propose a match, quietly schedule, and secure the reservation (well, eventually) for dates and double dates. Your only job is being good company.
      </p>
      <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 10 }}>
        {[
          { n: '01', label: 'London-based meetups (for now)', sub: 'No long-distance heartbreak.' },
          { n: '02', label: 'Neighbourhood-aware', sub: 'Dates that respect the Tube map.' },
          { n: '03', label: 'AI first-date planner', sub: 'One suggestion, thoughtfully made.' },
        ].map(({ n, label, sub }) => (
          <div key={n} style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
            <div style={{ width: 36, height: 36, borderRadius: 100, flexShrink: 0, background: t.paperAlt, color: t.terracotta, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: FONTS.mono, fontSize: 11, fontWeight: 700 }}>{n}</div>
            <div>
              <div style={{ fontFamily: FONTS.sans, fontSize: 14, fontWeight: 600, color: t.ink }}>{label}</div>
              <div style={{ fontFamily: FONTS.sans, fontSize: 12.5, color: t.inkMute, marginTop: 1 }}>{sub}</div>
            </div>
          </div>
        ))}
      </div>
      <button
        data-testid="onb-agree-terms"
        onClick={() => setAgreedToTerms(!agreedToTerms)}
        style={{ all: 'unset', cursor: 'pointer', display: 'flex', alignItems: 'flex-start', gap: 12, marginTop: 8, padding: '14px 16px', borderRadius: 14, background: agreedToTerms ? `${t.terracotta}12` : t.paperAlt, border: `1px solid ${agreedToTerms ? t.terracotta : t.line}`, transition: 'all 200ms' }}
      >
        <div style={{ width: 20, height: 20, borderRadius: 6, flexShrink: 0, marginTop: 1, background: agreedToTerms ? t.terracotta : 'transparent', border: `1.5px solid ${agreedToTerms ? t.terracotta : t.inkMute}`, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 200ms' }}>
          {agreedToTerms && <svg width="11" height="9" viewBox="0 0 11 9" fill="none"><path d="M1 4L4 7.5L10 1" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>}
        </div>
        <span style={{ fontFamily: FONTS.sans, fontSize: 13, lineHeight: 1.5, color: t.inkSoft }}>
          I agree to the{' '}
          <span
            onClick={e => { e.stopPropagation(); Browser.open({ url: 'https://preminade.com/terms' }); }}
            style={{ color: t.terracotta, textDecoration: 'underline', textDecorationColor: `${t.terracotta}60`, cursor: 'pointer' }}
          >Terms & Conditions</span>
          {' '}and{' '}
          <span
            onClick={e => { e.stopPropagation(); Browser.open({ url: 'https://preminade.com/privacy' }); }}
            style={{ color: t.terracotta, textDecoration: 'underline', textDecorationColor: `${t.terracotta}60`, cursor: 'pointer' }}
          >Privacy Policy</span>
        </span>
      </button>
    </div>
  );
}

function OnbMode({
  t, signupMode, setSignupMode, isInvitee, inviteChecked, inviteInfo,
  showCodeInput, setShowCodeInput, codeValue, setCodeValue, codeError, codeChecking, onSubmitCode,
}: {
  t: Theme;
  signupMode: SignupMode; setSignupMode: (v: SignupMode) => void;
  isInvitee: boolean; inviteChecked: boolean;
  inviteInfo: { inviterName: string; inviterPhoto: string | null } | null;
  showCodeInput: boolean; setShowCodeInput: (v: boolean) => void;
  codeValue: string; setCodeValue: (v: string) => void;
  codeError: string; codeChecking: boolean; onSubmitCode: () => void;
}) {
  if (isInvitee) {
    if (!inviteChecked) {
      return (
        <div style={{ padding: '80px 28px', textAlign: 'center' }}>
          <div style={{ width: 26, height: 26, borderRadius: '50%', border: `2px solid ${t.terracotta}`, borderTopColor: 'transparent', animation: 'spin 0.8s linear infinite', margin: '0 auto' }} />
        </div>
      );
    }
    return (
      <div style={{ padding: '30px 28px' }}>
        <h2 style={{ fontFamily: FONTS.display, fontStyle: 'italic', fontSize: 32, margin: '0 0 6px', color: t.ink, letterSpacing: -0.4, fontWeight: 400 }}>
          You're joining<br />as a couple.
        </h2>
        <p style={{ fontFamily: FONTS.sans, fontSize: 13.5, color: t.inkMute, margin: 0 }}>
          Finish setting up your own profile — you'll be linked automatically.
        </p>
        <div style={{
          marginTop: 26, display: 'flex', alignItems: 'center', gap: 14,
          padding: '16px 18px', borderRadius: 16,
          background: `${t.gold}12`, border: `1px solid ${t.gold}33`,
        }}>
          {inviteInfo?.inviterPhoto ? (
            <img src={inviteInfo.inviterPhoto} alt="" style={{ width: 44, height: 44, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
          ) : (
            <div style={{
              width: 44, height: 44, borderRadius: '50%', flexShrink: 0,
              background: `linear-gradient(135deg, ${t.gold}66, ${t.terracotta}66)`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <span style={{ fontFamily: FONTS.display, fontStyle: 'italic', fontSize: 18, color: t.paper }}>
                {(inviteInfo?.inviterName ?? '?').charAt(0)}
              </span>
            </div>
          )}
          <div>
            <div style={{ fontFamily: FONTS.sans, fontSize: 14, fontWeight: 600, color: t.ink }}>{inviteInfo?.inviterName ?? 'Your partner'}</div>
            <div style={{ fontFamily: FONTS.sans, fontSize: 12, color: t.inkMute, marginTop: 1 }}>invited you to Preminade</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '30px 28px' }}>
      <h2 style={{ fontFamily: FONTS.display, fontStyle: 'italic', fontSize: 32, margin: '0 0 6px', color: t.ink, letterSpacing: -0.4, fontWeight: 400 }}>
        How are you here?
      </h2>
      <p style={{ fontFamily: FONTS.sans, fontSize: 13.5, color: t.inkMute, margin: '0 0 28px' }}>You can change this later.</p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <button
          data-testid="onb-mode-solo"
          onClick={() => { hapticSelect(); setSignupMode('solo'); }}
          className="pm-press-soft"
          style={{
            all: 'unset', cursor: 'pointer', padding: '18px 20px', borderRadius: 18,
            background: signupMode === 'solo' ? t.ink : t.card,
            border: `1.5px solid ${signupMode === 'solo' ? t.ink : t.line}`,
            transition: 'all 150ms',
          }}
        >
          <div style={{ fontFamily: FONTS.sans, fontSize: 16, fontWeight: 600, color: signupMode === 'solo' ? t.paper : t.ink }}>Here solo</div>
          <div style={{ fontFamily: FONTS.sans, fontSize: 12.5, color: signupMode === 'solo' ? `${t.paper}99` : t.inkMute, marginTop: 4, lineHeight: 1.4 }}>
            Match one-on-one. Couples mode unlocks later if you meet someone.
          </div>
        </button>
        <button
          data-testid="onb-mode-couple"
          onClick={() => { hapticSelect(); setSignupMode('couple'); }}
          className="pm-press-soft"
          style={{
            all: 'unset', cursor: 'pointer', padding: '18px 20px', borderRadius: 18,
            background: signupMode === 'couple' ? t.ink : t.card,
            border: `1.5px solid ${signupMode === 'couple' ? t.ink : t.line}`,
            transition: 'all 150ms',
          }}
        >
          <div style={{ fontFamily: FONTS.sans, fontSize: 16, fontWeight: 600, color: signupMode === 'couple' ? t.paper : t.ink }}>With my partner</div>
          <div style={{ fontFamily: FONTS.sans, fontSize: 12.5, color: signupMode === 'couple' ? `${t.paper}99` : t.inkMute, marginTop: 4, lineHeight: 1.4 }}>
            Set up as a couple and get matched with other couples for double dates.
          </div>
        </button>
      </div>

      {!showCodeInput ? (
        <button
          onClick={() => setShowCodeInput(true)}
          style={{
            all: 'unset', cursor: 'pointer', display: 'block', width: '100%', textAlign: 'center',
            marginTop: 22, fontFamily: FONTS.sans, fontSize: 12.5, color: t.inkMute,
            textDecoration: 'underline', textDecorationColor: `${t.inkMute}55`,
          }}
        >
          Have an invite code?
        </button>
      ) : (
        <div style={{ marginTop: 22 }}>
          <Field t={t} label="Invite code">
            <input
              value={codeValue}
              onChange={e => setCodeValue(e.target.value.toUpperCase())}
              placeholder="PM-XXXXXX"
              style={{ ...inputStyle(t), fontFamily: FONTS.mono, letterSpacing: 1 }}
            />
          </Field>
          {codeError && (
            <div style={{ fontFamily: FONTS.sans, fontSize: 12, color: t.terracotta, marginTop: 8 }}>{codeError}</div>
          )}
          <button
            onClick={onSubmitCode}
            disabled={codeChecking}
            style={{
              all: 'unset', cursor: codeChecking ? 'default' : 'pointer', display: 'block', width: '100%',
              textAlign: 'center', marginTop: 12, padding: '12px 0', borderRadius: 14,
              background: t.card, border: `1px solid ${t.line}`,
              fontFamily: FONTS.sans, fontSize: 13.5, fontWeight: 600, color: t.ink,
              opacity: codeChecking ? 0.6 : 1,
            }}
          >
            {codeChecking ? 'Checking…' : 'Apply code'}
          </button>
        </div>
      )}
    </div>
  );
}

function OnbBasics({ t, displayName, setDisplayName, dateOfBirth, setDateOfBirth, isCouple }: {
  t: Theme; displayName: string; setDisplayName: (v: string) => void;
  dateOfBirth: string; setDateOfBirth: (v: string) => void; isCouple: boolean;
}) {
  return (
    <div style={{ padding: '30px 28px' }}>
      <h2 style={{ fontFamily: FONTS.display, fontStyle: 'italic', fontSize: 32, margin: '0 0 6px', color: t.ink, letterSpacing: -0.4, fontWeight: 400 }}>
        Let's start with<br />the basics.
      </h2>
      <p style={{ fontFamily: FONTS.sans, fontSize: 13.5, color: t.inkMute, margin: 0 }}>Don't worry, we'll judge later.</p>
      <div style={{ marginTop: 30, display: 'flex', flexDirection: 'column', gap: 16 }}>
        <Field t={t} label="First name">
          <input data-testid="onb-name" value={displayName} onChange={e => setDisplayName(e.target.value)} placeholder="Your name" style={inputStyle(t)} />
        </Field>
        <Field t={t} label="Date of birth">
          <input
            data-testid="onb-dob"
            type="date"
            value={dateOfBirth}
            onChange={e => setDateOfBirth(e.target.value)}
            max={dobBoundary(18)}
            min={dobBoundary(100)}
            style={inputStyle(t)}
          />
          {dateOfBirth.length === 10 && calendarAge(dateOfBirth) < 18 && (
            <div style={{ fontFamily: FONTS.sans, fontSize: 12, color: '#C0562A', marginTop: 6 }}>
              You must be 18 or older to join Preminade.
            </div>
          )}
        </Field>
        <Field t={t} label="I'm here for">
          <div style={{
            padding: '12px 0', borderRadius: 12, textAlign: 'center',
            fontFamily: FONTS.sans, fontSize: 14, fontWeight: 500,
            background: t.ink, color: t.paper, border: `1px solid ${t.ink}`,
          }}>
            {isCouple ? 'Couples mode' : 'Singles'}
          </div>
        </Field>
      </div>
    </div>
  );
}

// Age in whole years from a YYYY-MM-DD string, by the calendar.
//
// Deliberately NOT elapsed-milliseconds / 365.25: averaging leap years lands
// roughly half a day short on an exact birthday, so someone signing up ON
// their 18th birthday was told "You must be 18 or older" — while the API,
// which does compute this by the calendar, would have accepted them. Same
// shape as the 50km/31mi slider bug: a client-side limit disagreeing with
// the server's.
//
// The date parts are read off the string rather than via new Date(iso),
// because that parses as UTC midnight and can report the wrong day for
// anyone west of Greenwich.
export function calendarAge(isoDate: string): number {
  const [y, m, d] = isoDate.split('-').map(Number);
  if (!y || !m || !d) return NaN;
  const today = new Date();
  let age = today.getFullYear() - y;
  const monthDiff = today.getMonth() + 1 - m;
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < d)) age--;
  return age;
}

// The latest date of birth that is still `years` old today — i.e. today's
// date shifted back that many years. Built from local parts for the same
// reason as above.
function dobBoundary(years: number): string {
  const d = new Date();
  d.setFullYear(d.getFullYear() - years);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const GENDER_OPTIONS: { key: Gender; label: string; emoji: string }[] = [
  { key: 'man', label: 'Man', emoji: '♂' },
  { key: 'woman', label: 'Woman', emoji: '♀' },
  { key: 'nonbinary', label: 'Non-binary', emoji: '⚧' },
  { key: 'other', label: 'Other', emoji: '○' },
];

function OnbGender({ t, gender, setGender, ethnicity, setEthnicity, nationality, setNationality }: {
  t: Theme; gender: Gender | ''; setGender: (v: Gender) => void;
  ethnicity: string; setEthnicity: (v: string) => void;
  nationality: string; setNationality: (v: string) => void;
}) {
  return (
    <div data-clarity-mask="true" style={{ padding: '30px 28px' }}>
      <h2 style={{ fontFamily: FONTS.display, fontStyle: 'italic', fontSize: 32, margin: '0 0 6px', color: t.ink, letterSpacing: -0.4, fontWeight: 400 }}>
        I identify as...
      </h2>
      <p style={{ fontFamily: FONTS.sans, fontSize: 13.5, color: t.inkMute, margin: '0 0 28px' }}>This helps us show you to the right people.</p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {GENDER_OPTIONS.map(({ key, label, emoji }) => (
          <button key={key} data-testid={`onb-gender-${key}`} onClick={() => { hapticSelect(); setGender(key); }} className="pm-press-soft" style={{
            all: 'unset', cursor: 'pointer', padding: '16px 20px', borderRadius: 16,
            background: gender === key ? t.ink : t.card,
            border: `1.5px solid ${gender === key ? t.ink : t.line}`,
            display: 'flex', alignItems: 'center', gap: 14,
            transition: 'all 150ms',
          }}>
            <span style={{ fontSize: 20, width: 28, textAlign: 'center' }}>{emoji}</span>
            <span style={{ fontFamily: FONTS.sans, fontSize: 15, fontWeight: 500, color: gender === key ? t.paper : t.ink }}>{label}</span>
            {gender === key && <span style={{ marginLeft: 'auto' }}>{Icon.check({ s: 16, c: t.paper, w: 2.5 })}</span>}
          </button>
        ))}
      </div>

      {/* Optional identity fields — skippable, never used for matching */}
      <div style={{ marginTop: 28 }}>
        <div style={{ fontFamily: FONTS.sans, fontSize: 11, textTransform: 'uppercase', letterSpacing: 1.2, color: t.inkMute, marginBottom: 4 }}>
          Optional
        </div>
        <div style={{ fontFamily: FONTS.sans, fontSize: 12, color: t.inkMute, marginBottom: 12, lineHeight: 1.5 }}>
          Shown on your profile if set — skip freely, edit anytime from You.
        </div>

        <div style={{ fontFamily: FONTS.sans, fontSize: 12.5, color: t.inkSoft, marginBottom: 8 }}>Ethnicity</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7, marginBottom: 16 }}>
          {ETHNICITY_OPTIONS.map(({ key, label }) => {
            const selected = ethnicity === key;
            return (
              <button key={key} onClick={() => { hapticSelect(); setEthnicity(selected ? '' : key); }} className="pm-press-soft" style={{
                all: 'unset', cursor: 'pointer', padding: '7px 13px', borderRadius: 100,
                fontFamily: FONTS.sans, fontSize: 12.5, fontWeight: 500,
                background: selected ? t.ink : t.card,
                color: selected ? t.paper : t.ink,
                border: `1px solid ${selected ? t.ink : t.line}`,
                transition: 'all 150ms',
              }}>{label}</button>
            );
          })}
        </div>

        <div style={{ fontFamily: FONTS.sans, fontSize: 12.5, color: t.inkSoft, marginBottom: 8 }}>Nationality</div>
        <select
          value={nationality}
          onChange={e => setNationality(e.target.value)}
          style={{
            width: '100%', padding: '13px 14px', borderRadius: 14, boxSizing: 'border-box',
            background: t.card, border: `1px solid ${t.line}`,
            fontFamily: FONTS.sans, fontSize: 15, color: nationality ? t.ink : t.inkMute,
            outline: 'none', appearance: 'none', WebkitAppearance: 'none',
          }}
        >
          <option value="">Prefer not to say</option>
          {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>
    </div>
  );
}

function OnbSeeking({ t, seekingGenders, setSeeking }: { t: Theme; seekingGenders: Gender[]; setSeeking: (v: Gender[]) => void }) {
  const toggle = (key: Gender) => {
    hapticSelect();
    setSeeking(seekingGenders.includes(key)
      ? seekingGenders.filter(g => g !== key)
      : [...seekingGenders, key]);
  };
  return (
    <div data-clarity-mask="true" style={{ padding: '30px 28px' }}>
      <h2 style={{ fontFamily: FONTS.display, fontStyle: 'italic', fontSize: 32, margin: '0 0 6px', color: t.ink, letterSpacing: -0.4, fontWeight: 400 }}>
        I'm interested in...
      </h2>
      <p style={{ fontFamily: FONTS.sans, fontSize: 13.5, color: t.inkMute, margin: '0 0 28px' }}>Pick all that apply.</p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {GENDER_OPTIONS.map(({ key, label, emoji }) => {
          const selected = seekingGenders.includes(key);
          return (
            <button key={key} data-testid={`onb-seeking-${key}`} onClick={() => toggle(key)} className="pm-press-soft" style={{
              all: 'unset', cursor: 'pointer', padding: '16px 20px', borderRadius: 16,
              background: selected ? t.ink : t.card,
              border: `1.5px solid ${selected ? t.ink : t.line}`,
              display: 'flex', alignItems: 'center', gap: 14,
              transition: 'all 150ms',
            }}>
              <span style={{ fontSize: 20, width: 28, textAlign: 'center' }}>{emoji}</span>
              <span style={{ fontFamily: FONTS.sans, fontSize: 15, fontWeight: 500, color: selected ? t.paper : t.ink }}>{label}</span>
              {selected && <span style={{ marginLeft: 'auto' }}>{Icon.check({ s: 16, c: t.paper, w: 2.5 })}</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function OnbTaste({ t, activities, setActivities, budget, setBudget, vibeKeys, setVibeKeys }: {
  t: Theme;
  activities: Record<string, number>; setActivities: (v: Record<string, number>) => void;
  budget: number; setBudget: (v: number) => void;
  vibeKeys: string[]; setVibeKeys: (v: string[]) => void;
}) {
  const toggleVibe = (key: string) => {
    hapticSelect();
    if (vibeKeys.includes(key)) {
      if (vibeKeys.length === 1) return; // keep at least one
      setVibeKeys(vibeKeys.filter(k => k !== key));
    } else {
      setVibeKeys([...vibeKeys, key]);
    }
  };
  return (
    <div style={{ padding: '30px 28px', display: 'flex', flexDirection: 'column', gap: 28 }}>
      <div>
        <h2 style={{ fontFamily: FONTS.display, fontStyle: 'italic', fontSize: 32, margin: '0 0 6px', color: t.ink, letterSpacing: -0.4, fontWeight: 400 }}>
          What are you into?
        </h2>
        <p style={{ fontFamily: FONTS.sans, fontSize: 13.5, color: t.inkMute, margin: 0 }}>Rate each category 1–5.</p>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {ACTIVITY_CATEGORIES.map(({ key, label }) => (
          <div key={key}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
              <span style={{ fontFamily: FONTS.sans, fontSize: 13.5, color: t.ink }}>{label}</span>
              <span style={{ fontFamily: FONTS.mono, fontSize: 12, color: t.terracotta }}>{activities[key]}/5</span>
            </div>
            <input data-testid={`onb-activity-${key}`} type="range" min={1} max={5} step={1} value={activities[key]}
              onChange={e => setActivities({ ...activities, [key]: parseInt(e.target.value) })}
              style={{ width: '100%', accentColor: t.terracotta }} />
          </div>
        ))}
      </div>

      <div>
        <div style={{ fontFamily: FONTS.sans, fontSize: 11, textTransform: 'uppercase', letterSpacing: 1.2, color: t.inkMute, marginBottom: 10 }}>Typical date budget</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
          {BUDGET_OPTIONS.map(({ value, label, sub }) => (
            <button key={value} data-testid={`onb-budget-${value}`} onClick={() => { hapticSelect(); setBudget(value); }} className="pm-press-soft" style={{
              all: 'unset', cursor: 'pointer', padding: '10px 6px', borderRadius: 12, textAlign: 'center',
              background: budget === value ? t.ink : t.card,
              border: `1px solid ${budget === value ? t.ink : t.line}`,
              transition: 'all 150ms',
            }}>
              <div style={{ fontFamily: FONTS.sans, fontSize: 14, fontWeight: 600, color: budget === value ? t.paper : t.ink }}>{label}</div>
              <div style={{ fontFamily: FONTS.sans, fontSize: 10, color: budget === value ? `${t.paper}99` : t.inkMute, marginTop: 2 }}>{sub}</div>
            </button>
          ))}
        </div>
      </div>

      <div>
        <div style={{ fontFamily: FONTS.sans, fontSize: 11, textTransform: 'uppercase', letterSpacing: 1.2, color: t.inkMute, marginBottom: 10 }}>Your vibe</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {VIBE_OPTIONS.map(({ key, label, sub }) => {
            const active = vibeKeys.includes(key);
            return (
              <button key={key} data-testid={`onb-vibe-${key}`} onClick={() => toggleVibe(key)} className="pm-press-soft" style={{
                all: 'unset', cursor: 'pointer', padding: '12px 16px', borderRadius: 14,
                background: active ? t.ink : t.card,
                border: `1px solid ${active ? t.ink : t.line}`,
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                transition: 'all 150ms',
              }}>
                <div>
                  <div style={{ fontFamily: FONTS.sans, fontSize: 14, fontWeight: 600, color: active ? t.paper : t.ink }}>{label}</div>
                  <div style={{ fontFamily: FONTS.sans, fontSize: 12, color: active ? `${t.paper}99` : t.inkMute }}>{sub}</div>
                </div>
                {active && Icon.check({ s: 16, c: t.paper, w: 2.5 })}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function OnbLocation({ t, postcode, setPostcode, radiusValue, setRadiusValue, distanceUnit, setDistanceUnit }: {
  t: Theme; postcode: string; setPostcode: (v: string) => void;
  radiusValue: number; setRadiusValue: (v: number) => void;
  distanceUnit: 'miles' | 'km'; setDistanceUnit: (v: 'miles' | 'km') => void;
}) {
  const handleUnitToggle = (unit: 'miles' | 'km') => {
    if (unit === distanceUnit) return;
    const max = unit === 'km' ? 48 : 30;
    if (unit === 'km') {
      setRadiusValue(Math.min(max, Math.round(radiusValue * 1.60934)));
    } else {
      setRadiusValue(Math.min(max, Math.round(radiusValue / 1.60934)));
    }
    setDistanceUnit(unit);
  };

  return (
    <div style={{ padding: '30px 28px' }}>
      <h2 style={{ fontFamily: FONTS.display, fontStyle: 'italic', fontSize: 32, margin: '0 0 6px', color: t.ink, letterSpacing: -0.4, fontWeight: 400 }}>
        Where do<br />you roam?
      </h2>
      <p style={{ fontFamily: FONTS.sans, fontSize: 13.5, color: t.inkMute, margin: 0 }}>
        We'll prioritise people who won't make you get on the Jubilee line.
      </p>
      <div style={{ marginTop: 30, display: 'flex', flexDirection: 'column', gap: 16 }}>
        <Field t={t} label="Your postcode">
          <input data-testid="onb-postcode" value={postcode} onChange={e => setPostcode(e.target.value.toUpperCase())} placeholder="E.g. N16 0JR" style={inputStyle(t)} />
        </Field>
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <div style={{ fontFamily: FONTS.sans, fontSize: 11, textTransform: 'uppercase', letterSpacing: 1.2, color: t.inkMute }}>
              Max travel distance — {radiusValue} {distanceUnit}
            </div>
            <div style={{ display: 'flex', gap: 4, background: t.card, padding: 2, borderRadius: 6, border: `1px solid ${t.line}` }}>
              <button data-testid="onb-unit-miles" onClick={() => handleUnitToggle('miles')} style={{ all: 'unset', cursor: 'pointer', padding: '2px 8px', borderRadius: 4, background: distanceUnit === 'miles' ? t.ink : 'transparent', color: distanceUnit === 'miles' ? t.paper : t.inkMute, fontFamily: FONTS.sans, fontSize: 10, fontWeight: 600 }}>MI</button>
              <button data-testid="onb-unit-km" onClick={() => handleUnitToggle('km')} style={{ all: 'unset', cursor: 'pointer', padding: '2px 8px', borderRadius: 4, background: distanceUnit === 'km' ? t.ink : 'transparent', color: distanceUnit === 'km' ? t.paper : t.inkMute, fontFamily: FONTS.sans, fontSize: 10, fontWeight: 600 }}>KM</button>
            </div>
          </div>
          {/* Km max is 48, not 50 — the backend caps locationRadiusMiles at 30, and
              50km × 0.621371 rounds to 31 miles, so 50 would fail onboarding
              validation with a generic "Validation failed" and no clue why. */}
          <input data-testid="onb-radius" type="range" min={1} max={distanceUnit === 'miles' ? 30 : 48} step={1} value={radiusValue}
            onChange={e => setRadiusValue(parseInt(e.target.value))}
            style={{ width: '100%', accentColor: t.terracotta }} />
          <div style={{ display: 'flex', justifyContent: 'space-between', fontFamily: FONTS.mono, fontSize: 11, color: t.inkMute, marginTop: 4 }}>
            <span>1 {distanceUnit}</span><span>{distanceUnit === 'miles' ? 30 : 48} {distanceUnit}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function OnbSpark({ t, answers, setAnswers }: {
  t: Theme; answers: string[]; setAnswers: (v: string[]) => void;
}) {
  return (
    <div style={{ padding: '30px 28px' }}>
      <h2 style={{ fontFamily: FONTS.display, fontStyle: 'italic', fontSize: 32, margin: '0 0 6px', color: t.ink, letterSpacing: -0.4, fontWeight: 400 }}>
        Three things<br />about you.
      </h2>
      <p style={{ fontFamily: FONTS.sans, fontSize: 13.5, color: t.inkMute, margin: 0 }}>Shown after a mutual Spark reveal.</p>
      <div style={{ marginTop: 28, display: 'flex', flexDirection: 'column', gap: 20 }}>
        {SPARK_PROMPTS.map((prompt, i) => (
          <div key={i}>
            <div style={{ fontFamily: FONTS.sans, fontSize: 11, textTransform: 'uppercase', letterSpacing: 1.2, color: t.inkMute, marginBottom: 8 }}>{prompt}</div>
            <textarea
              data-testid={`onb-spark-${i}`}
              value={answers[i]}
              onChange={e => { const next = [...answers]; next[i] = e.target.value; setAnswers(next); }}
              placeholder="Your answer..."
              rows={3}
              style={{
                ...inputStyle(t),
                resize: 'none', height: 'auto',
                fontFamily: FONTS.display, fontStyle: 'italic', fontSize: 16, lineHeight: 1.4,
              }}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

function OnbPhoto({ t, previewUrl, preparing, onPick }: {
  t: Theme; previewUrl: string | null; preparing: boolean; onPick: (file: File) => void;
}) {
  return (
    <div style={{ padding: '30px 28px' }}>
      <h2 style={{ fontFamily: FONTS.display, fontStyle: 'italic', fontSize: 32, margin: '0 0 6px', color: t.ink, letterSpacing: -0.4, fontWeight: 400 }}>
        Put a face<br />to the name.
      </h2>
      <p style={{ fontFamily: FONTS.sans, fontSize: 13.5, color: t.inkMute, margin: 0, lineHeight: 1.5 }}>
        Add a clear photo of you — it's the first thing people see. You can add more later from You.
      </p>
      <label
        data-testid="onb-photo-pick"
        className="pm-press-soft"
        style={{
          position: 'relative', cursor: preparing ? 'default' : 'pointer', display: 'block', overflow: 'hidden',
          margin: '28px auto 0', width: '100%', maxWidth: 260, aspectRatio: '4 / 5', borderRadius: 22,
          background: previewUrl ? t.card : t.paperAlt,
          border: previewUrl ? `1px solid ${t.line}` : `1.5px dashed ${t.inkMute}66`,
        }}
      >
        <input
          data-testid="onb-photo-input"
          type="file"
          accept="image/*"
          disabled={preparing}
          style={{ display: 'none' }}
          onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; if (f) onPick(f); }}
        />
        {previewUrl ? (
          <>
            <img data-testid="onb-photo-preview" src={previewUrl} alt="Your photo" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
            <span style={{
              position: 'absolute', left: '50%', bottom: 12, transform: 'translateX(-50%)',
              padding: '7px 14px', borderRadius: 100, background: `${t.ink}CC`, color: t.paper,
              fontFamily: FONTS.sans, fontSize: 12.5, fontWeight: 600, whiteSpace: 'nowrap',
            }}>Change photo</span>
          </>
        ) : (
          <span style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12 }}>
            {preparing ? (
              <span style={{ width: 26, height: 26, borderRadius: '50%', border: `2px solid ${t.terracotta}`, borderTopColor: 'transparent', animation: 'spin 0.8s linear infinite' }} />
            ) : (
              <>
                <span style={{ width: 52, height: 52, borderRadius: 100, background: t.ink, color: t.paper, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, fontWeight: 300, lineHeight: 1 }}>+</span>
                <span style={{ fontFamily: FONTS.sans, fontSize: 14, fontWeight: 600, color: t.ink }}>Add a photo</span>
              </>
            )}
          </span>
        )}
      </label>
      <div style={{ marginTop: 18, fontFamily: FONTS.sans, fontSize: 12, color: t.inkMute, lineHeight: 1.5, textAlign: 'center' }}>
        Just you, face visible — no group shots or sunglasses.
      </div>
    </div>
  );
}

function OnbInvitePartner({ t, partnerEmail, setPartnerEmail }: {
  t: Theme; partnerEmail: string; setPartnerEmail: (v: string) => void;
}) {
  return (
    <div style={{ padding: '30px 28px' }}>
      <h2 style={{ fontFamily: FONTS.display, fontStyle: 'italic', fontSize: 32, margin: '0 0 6px', color: t.ink, letterSpacing: -0.4, fontWeight: 400 }}>
        Bring your<br />partner in.
      </h2>
      <p style={{ fontFamily: FONTS.sans, fontSize: 13.5, color: t.inkMute, margin: 0, lineHeight: 1.5 }}>
        They'll set up their own profile — takes two minutes. You'll be paired up as a couple once they're in.
      </p>
      <div style={{ marginTop: 30 }}>
        <Field t={t} label="Partner's email (optional)">
          <input
            type="email"
            value={partnerEmail}
            onChange={e => setPartnerEmail(e.target.value)}
            placeholder="their@email.com"
            style={inputStyle(t)}
          />
        </Field>
        <div style={{ marginTop: 10, fontFamily: FONTS.sans, fontSize: 12, color: t.inkMute, lineHeight: 1.5 }}>
          No email handy? You'll get a link and code to share after this.
        </div>
      </div>
    </div>
  );
}

function Field({ t, label, children }: { t: Theme; label: string; children: React.ReactNode }) {
  return (
    <div>
      <div style={{ fontFamily: FONTS.sans, fontSize: 11, textTransform: 'uppercase', letterSpacing: 1.2, color: t.inkMute, marginBottom: 6 }}>{label}</div>
      {children}
    </div>
  );
}

function inputStyle(t: Theme): React.CSSProperties {
  return {
    width: '100%', padding: '14px 16px', borderRadius: 14, boxSizing: 'border-box',
    background: t.card, border: `1px solid ${t.line}`,
    fontFamily: FONTS.sans, fontSize: 15, color: t.ink,
    outline: 'none',
  };
}
